import React, { useState, useEffect, useMemo } from "react"
import { Table, Form, InputGroup, Button } from "react-bootstrap"
import { toast } from "react-toastify"
import * as XLSX from "xlsx"
import { formatDateForDisplay } from "../../utils/dateUtils"

const DataTable = ({
  data = [],
  columns = [],
  loading = false,
  error = null,
  emptyMessage = "Δεν βρέθηκαν αποτελέσματα",
  onRowClick = null,
  exportFilename = "expert-export"
}) => {
  // Internal state management
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(10)
  const [searchTerm, setSearchTerm] = useState("")
  const [sortConfig, setSortConfig] = useState({ key: null, direction: "asc" })

  // Handle sort
  const handleSort = key => {
    let direction = "asc"
    if (sortConfig.key === key && sortConfig.direction === "asc") {
      direction = "desc"
    }
    setSortConfig({ key, direction })
    setCurrentPage(1)
  }

  // Compare values for sorting
  const compareValues = (aValue, bValue, type) => {
    // Handle null/undefined/dash values
    const isDash = val => val === "-" || val === "—" || val === "–"
    const isEmpty = val => val == null || isDash(val)

    // Handle different types
    switch (type) {
      case "date":
        // Handle empty/invalid dates - put them at the beginning
        if (isEmpty(aValue)) return -1
        if (isEmpty(bValue)) return 1
        if (isEmpty(aValue) && isEmpty(bValue)) return 0

        // Convert date strings to timestamps
        let dateA, dateB
        try {
          // Try to parse the date strings
          if (typeof aValue === "string") {
            // Handle Greek date format (dd/mm/yyyy)
            const [day, month, year] = aValue.split("/")

            // Validate date parts
            if (!day || !month || !year) {
              return -1
            }

            // Convert to numbers and validate
            const dayNum = parseInt(day, 10)
            const monthNum = parseInt(month, 10)
            const yearNum = parseInt(year, 10)

            // Validate date ranges
            if (
              isNaN(dayNum) ||
              isNaN(monthNum) ||
              isNaN(yearNum) ||
              dayNum < 1 ||
              dayNum > 31 ||
              monthNum < 1 ||
              monthNum > 12 ||
              yearNum < 1900 ||
              yearNum > 2100
            ) {
              return -1
            }

            dateA = new Date(yearNum, monthNum - 1, dayNum)
          } else {
            dateA = new Date(aValue)
          }

          if (typeof bValue === "string") {
            // Handle Greek date format (dd/mm/yyyy)
            const [day, month, year] = bValue.split("/")

            // Validate date parts
            if (!day || !month || !year) {
              return 1
            }

            // Convert to numbers and validate
            const dayNum = parseInt(day, 10)
            const monthNum = parseInt(month, 10)
            const yearNum = parseInt(year, 10)

            // Validate date ranges
            if (
              isNaN(dayNum) ||
              isNaN(monthNum) ||
              isNaN(yearNum) ||
              dayNum < 1 ||
              dayNum > 31 ||
              monthNum < 1 ||
              monthNum > 12 ||
              yearNum < 1900 ||
              yearNum > 2100
            ) {
              return 1
            }

            dateB = new Date(yearNum, monthNum - 1, dayNum)
          } else {
            dateB = new Date(bValue)
          }

          // Check if dates are valid before getting timestamps
          if (isNaN(dateA.getTime())) {
            return -1
          }
          if (isNaN(dateB.getTime())) {
            return 1
          }

          // Get timestamps only for valid dates
          dateA = dateA.getTime()
          dateB = dateB.getTime()
        } catch (e) {
          return 0
        }

        return dateA - dateB
      case "number":
        // Convert to numbers and handle non-numeric values
        const numA = isDash(aValue) ? NaN : Number(aValue)
        const numB = isDash(bValue) ? NaN : Number(bValue)

        // Handle non-numeric values
        if (isNaN(numA)) return 1
        if (isNaN(numB)) return -1
        if (isNaN(numA) && isNaN(numB)) return 0

        return numA - numB
      default:
        // Default string comparison
        if (isDash(aValue)) return 1
        if (isDash(bValue)) return -1
        if (isDash(aValue) && isDash(bValue)) return 0

        aValue = aValue.toString().toLowerCase()
        bValue = bValue.toString().toLowerCase()
        if (aValue < bValue) return -1
        if (aValue > bValue) return 1
        return 0
    }
  }

  // Filter and sort data
  const filteredAndSortedData = useMemo(() => {
    if (!data || data.length === 0) return []

    let result = [...data]

    // Apply filters
    if (searchTerm) {
      result = result.filter(item => {
        return columns.some(column => {
          const value = column.render ? column.render(item)?.toString() : item[column.field]?.toString()
          return value?.toLowerCase().includes(searchTerm.toLowerCase())
        })
      })
    }

    // Apply sorting
    if (sortConfig.key) {
      result.sort((a, b) => {
        const column = columns.find(col => col.field === sortConfig.key)
        let aValue, bValue

        if (column.sortValue) {
          // Use sortValue function if provided
          aValue = column.sortValue(a)
          bValue = column.sortValue(b)
        } else {
          // Otherwise use render function or direct field value
          aValue = column.render ? column.render(a) : a[column.field]
          bValue = column.render ? column.render(b) : b[column.field]
        }

        const result = compareValues(aValue, bValue, column.type)
        return sortConfig.direction === "asc" ? result : -result
      })
    }

    return result
  }, [data, columns, searchTerm, sortConfig])

  // Calculate total items and pages
  const totalItems = filteredAndSortedData.length
  const totalPages = Math.ceil(totalItems / pageSize)

  // Get displayed data based on current page and page size
  const displayedData = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize
    const endIndex = startIndex + pageSize
    return filteredAndSortedData.slice(startIndex, endIndex)
  }, [filteredAndSortedData, currentPage, pageSize])

  // Reset to first page when data, search term, or sort changes
  useEffect(() => {
    setCurrentPage(1)
  }, [data, searchTerm, sortConfig])

  // Generate pagination numbers
  const getPaginationNumbers = () => {
    const pages = []
    const maxVisiblePages = 5
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
    let endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

    // Adjust start page if end page is maxed out
    startPage = Math.max(1, endPage - maxVisiblePages + 1)

    if (startPage > 1) {
      pages.push(1)
      if (startPage > 2) pages.push("...")
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i)
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) pages.push("...")
      pages.push(totalPages)
    }

    return pages
  }

  // Export to Excel function
  const handleExport = () => {
    try {
      // Create worksheet data
      const wsData = filteredAndSortedData.map(item => {
        const row = {}
        columns.forEach(column => {
          // Get the column value using render function or direct field access
          let value = null
          if (column.render) {
            if (!value?.props?.children) {
              value = column.render(item)
            }

            // Try to cleanup the rendered value for Excel export
            if (typeof value.props?.children === "string" || typeof value.props?.children === "number") {
              value = value.props.children
            }

            if (
              Array.isArray(value.props?.children) &&
              value.props.children.every(child => typeof child === "string" || typeof child === "number")
            ) {
              value = value.props.children.join(" ")
            }

            if (null === value) {
              value = "Error rendering column"
            }
          } else {
            value = item[column.field]
          }

          row[column.header] = value
        })
        return row
      })

      // Create worksheet
      const ws = XLSX.utils.json_to_sheet(wsData)

      // Create workbook
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, "Data")

      // Generate filename with timestamp
      const timestamp = formatDateForDisplay(new Date())
      const filename = `${exportFilename}-${timestamp}.xlsx`

      // Save file
      XLSX.writeFile(wb, filename)
      toast.success("Το αρχείο εξήχθη επιτυχώς", { autoClose: 3000 })
    } catch (error) {
      console.error("Export error:", error)
      toast.error("Σφάλμα κατά την εξαγωγή του αρχείου")
    }
  }

  // Handle error state
  if (error) {
    toast.error(error)
    return null
  }

  return (
    <div className="table-container">
      {/* Main Table */}
      <div className="table-responsive">
        <Table hover>
          <thead>
            <tr>
              {columns.map((column, index) => (
                <th
                  key={index}
                  onClick={() => column.field && handleSort(column.field)}
                  style={{
                    cursor: column.field ? "pointer" : "default",
                    userSelect: "none"
                  }}
                  className="position-relative">
                  <div className="d-flex align-items-center gap-2">
                    {column.header}
                    {column.field && (
                      <div className="d-flex flex-column" style={{ fontSize: "0.8em", lineHeight: 1 }}>
                        <i
                          className={`fas fa-caret-up ${
                            sortConfig.key === column.field && sortConfig.direction === "asc"
                              ? "text-primary"
                              : "text-muted"
                          }`}
                          style={{ marginBottom: "-3px" }}
                        />
                        <i
                          className={`fas fa-caret-down ${
                            sortConfig.key === column.field && sortConfig.direction === "desc"
                              ? "text-primary"
                              : "text-muted"
                          }`}
                        />
                      </div>
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-4">
                  <div className="spinner-border text-light" role="status">
                    <span className="visually-hidden">Φόρτωση...</span>
                  </div>
                </td>
              </tr>
            ) : filteredAndSortedData.length === 0 ? (
              <tr>
                <td colSpan={columns.length} className="text-center py-4">
                  {searchTerm ? "Δεν βρέθηκαν αποτελέσματα για την αναζήτησή σας" : emptyMessage}
                </td>
              </tr>
            ) : (
              displayedData.map((item, rowIndex) => (
                <tr
                  key={item.id || rowIndex}
                  onClick={() => onRowClick && onRowClick(item)}
                  style={{ cursor: onRowClick ? "pointer" : "default" }}
                  className={onRowClick ? "clickable-row" : ""}>
                  {columns.map((column, colIndex) => (
                    <td key={colIndex}>{column.render ? column.render(item) : item[column.field]}</td>
                  ))}
                </tr>
              ))
            )}
          </tbody>
        </Table>
      </div>

      {/* Table Footer */}
      {data.length > 0 && (
        <div className="table-footer d-flex justify-content-between align-items-center flex-wrap gap-3">
          <div className="d-flex align-items-center gap-3">
            <div className="d-flex align-items-center">
              <Form.Select
                size="sm"
                value={pageSize}
                onChange={e => {
                  setPageSize(Number(e.target.value))
                  setCurrentPage(1)
                }}>
                <option value="10">10</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100</option>
              </Form.Select>
              <span className="ms-2">ανά σελίδα</span>
            </div>

            <span className="text-muted">{totalItems} αποτελέσματα</span>

            <div className="d-flex align-items-center" style={{ width: "250px" }}>
              <InputGroup size="sm">
                <Form.Control
                  type="text"
                  placeholder="Αναζήτηση..."
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  className="border-end-0"
                />
              </InputGroup>
            </div>

            <Button
              variant="outline-light"
              size="sm"
              onClick={handleExport}
              disabled={loading || filteredAndSortedData.length === 0}>
              <i className="fas fa-file-excel me-2" />
              Εξαγωγή σε Excel
            </Button>
          </div>

          {/* Pagination */}
          <ul className="pagination mb-0">
            {getPaginationNumbers().map((page, index) => (
              <li
                key={index}
                className={`page-item ${page === currentPage ? "active" : ""} ${
                  page === "..." ? "disabled" : ""
                }`}>
                <button
                  className="page-link"
                  onClick={() => page !== "..." && setCurrentPage(page)}
                  disabled={page === "..."}>
                  {page}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  )
}

export default DataTable
